import { useEffect, useState } from "react";
import { createMeeting, listMeetings } from "../api/support.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface MeetingNote {
  id: string;
  title: string;
  summary: string;
  actionItems: string[] | null;
  attendees: string[] | null;
  clientId: string | null;
  meetingDate: string;
  createdAt: string;
}

export function SupportMeetingsPage() {
  const { token } = useAuth();
  const [meetings, setMeetings] = useState<MeetingNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: "",
    summary: "",
    meetingDate: new Date().toISOString().slice(0, 10),
    attendees: "",
    actionItems: "",
    clientId: "",
  });
  const [creating, setCreating] = useState(false);

  const reload = () => {
    void listMeetings(token).then((d) => {
      setMeetings((d as { items: MeetingNote[] }).items ?? []);
      setLoading(false);
    });
  };

  useEffect(() => {
    reload();
  }, []);

  const create = async () => {
    if (!form.title || !form.summary) return;
    setCreating(true);
    await createMeeting(token, {
      title: form.title,
      summary: form.summary,
      meetingDate: form.meetingDate,
      attendees: form.attendees ? form.attendees.split(",").map((s) => s.trim()) : undefined,
      actionItems: form.actionItems
        ? form.actionItems
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean)
        : undefined,
      clientId: form.clientId || undefined,
    });
    setShowForm(false);
    setCreating(false);
    setForm({
      title: "",
      summary: "",
      meetingDate: new Date().toISOString().slice(0, 10),
      attendees: "",
      actionItems: "",
      clientId: "",
    });
    reload();
  };

  return (
    <Layout>
      <div className="mx-auto max-w-4xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Meeting Notes</h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="rounded bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-500"
          >
            {showForm ? "Cancel" : "Record Meeting"}
          </button>
        </div>
        {showForm && (
          <div className="mb-6 space-y-3 rounded-lg border border-gray-700 bg-gray-900 p-4">
            <div className="grid grid-cols-2 gap-3">
              <input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Meeting title"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.meetingDate}
                onChange={(e) => setForm((f) => ({ ...f, meetingDate: e.target.value }))}
                type="date"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white"
              />
              <input
                value={form.clientId}
                onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}
                placeholder="Client ID (optional)"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.attendees}
                onChange={(e) => setForm((f) => ({ ...f, attendees: e.target.value }))}
                placeholder="Attendees (comma separated)"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
            </div>
            <textarea
              value={form.summary}
              onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
              placeholder="Meeting summary..."
              rows={4}
              className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
            />
            <textarea
              value={form.actionItems}
              onChange={(e) => setForm((f) => ({ ...f, actionItems: e.target.value }))}
              placeholder="Action items (one per line)..."
              rows={3}
              className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
            />
            <button
              onClick={() => void create()}
              disabled={creating}
              className="rounded bg-green-700 px-4 py-2 text-sm text-white hover:bg-green-600 disabled:opacity-50"
            >
              {creating ? "Saving..." : "Save Meeting Notes"}
            </button>
          </div>
        )}
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="space-y-4">
            {meetings.length === 0 && <p className="text-gray-400">No meeting notes yet.</p>}
            {meetings.map((m) => (
              <div key={m.id} className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="font-medium text-white">{m.title}</h3>
                  <span className="text-xs text-gray-500">
                    {new Date(m.meetingDate).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-gray-300">{m.summary}</p>
                {m.attendees && m.attendees.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {m.attendees.map((a) => (
                      <span
                        key={a}
                        className="rounded bg-gray-800 px-1.5 py-0.5 text-xs text-gray-400"
                      >
                        {a}
                      </span>
                    ))}
                  </div>
                )}
                {m.actionItems && m.actionItems.length > 0 && (
                  <div className="mt-3">
                    <p className="mb-1 text-xs text-gray-500">Action items:</p>
                    <ul className="space-y-0.5">
                      {m.actionItems.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                          <span className="mt-0.5 text-gray-600">•</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
